<main class="bg-black text-white min-h-screen overflow-x-hidden">

  <!-- ===== HERO ===== -->
  <section class="relative min-h-[85vh] flex items-center pt-24 pb-16 overflow-hidden page-hero-gradient">
    <div class="absolute -top-48 -left-24 w-[700px] h-[700px] bg-varroc-accent/5 rounded-full blur-3xl"></div>
    <div class="absolute inset-0 opacity-[0.025]" style="background-image: linear-gradient(rgba(0,170,230,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,170,230,0.5) 1px, transparent 1px); background-size: 60px 60px;"></div>

    <div class="relative z-10 max-w-7xl mx-auto px-6 w-full">
      <div class="grid md:grid-cols-2 gap-12 items-center">

        <!-- Left: text content -->
        <div>
          <a routerLink="/" class="back-nav" data-aos="fade-down">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 svg-stroke-gradient"><polyline points="15 18 9 12 15 6"></polyline></svg>
            Back
          </a>
          <h1 class="hero-col font-black mb-6" data-aos="fade-up" data-aos-delay="100">
            Intelligent Connected <span class="text-varroc-accent">Mobility &amp; IoT</span> Platform
          </h1>
          <p class="text-varroc-text-gray text-lg leading-relaxed mb-8 max-w-xl" data-aos="fade-up" data-aos-delay="200">
            A scalable, cloud-agnostic platform unifying device integration, streaming, analytics, lifecycle management, and customer engagement across vehicles, smart devices, and enterprise IoT.
          </p>
          <div class="flex flex-wrap gap-4 mb-10" data-aos="fade-up" data-aos-delay="300">
            <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-8 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all flex items-center gap-2">
              <i class='bx bx-calendar-check text-xl'></i> Book a Demo
            </a>
      
          </div>
          <!-- Platform stats -->
          <div class="flex flex-wrap gap-6" data-aos="fade-up" data-aos-delay="400">
            <div class="text-left">
              <div class="text-varroc-accent font-black text-2xl">{{ products.length }}</div>
              <div class="text-varroc-text-gray text-xs mt-1">Integrated Products</div>
            </div>
            <div class="text-left">
              <div class="text-varroc-accent font-black text-2xl">Cloud-Agnostic</div>
              <div class="text-varroc-text-gray text-xs mt-1">Deploy Anywhere</div>
            </div>
            <div class="text-left">
              <div class="text-varroc-accent font-black text-2xl">API-First</div>
              <div class="text-varroc-text-gray text-xs mt-1">Easy Integration</div>
            </div>
          </div>
        </div>

        <!-- Right: product cards grid (click to navigate) -->
        <div class="grid grid-cols-3 gap-2 md:gap-4" data-aos="fade-left" data-aos-delay="200">
          <a *ngFor="let p of products" [routerLink]="p.route || null" (click)="!p.route ? scrollTo(p.id) : null" class="feature-card !p-3 md:!p-5 text-left cursor-pointer group block no-underline">
            <img *ngIf="p.iconImage; else defaultCardIcon" [src]="p.iconImage" [alt]="p.title + ' icon'" class="w-7 h-7 md:w-10 md:h-10 object-contain mr-auto mb-1 md:mb-3" />
            <ng-template #defaultCardIcon>
              <i class='bx icon-gradient text-2xl md:text-4xl mb-1 md:mb-3' [class]="p.icon"></i>
            </ng-template>
            <div class="text-white font-bold text-xs md:text-sm leading-tight">{{ p.title }}</div>
            <div class="text-varroc-text-gray text-[10px] md:text-xs mt-0.5 md:mt-1 leading-tight hidden sm:block">{{ p.subtitle }}</div>
          </a>
        </div>

      </div>
    </div>
  </section>

  <!-- ===== STICKY NAV ===== -->
  <nav class="sticky-product-nav">
    <div class="max-w-7xl mx-auto px-6">
      <div class="flex overflow-x-auto">
        <a (click)="scrollTo('overview')" [class.active]="activeSection === 'overview'" class="cursor-pointer flex-shrink-0">
          <i class='bx bx-layer text-base'></i>
          Overview
        </a>
        <a *ngFor="let p of products" (click)="scrollTo(p.id)" [class.active]="activeSection === p.id" class="cursor-pointer flex-shrink-0">
          <img *ngIf="p.iconImage; else defaultStickyIcon" [src]="p.iconImage" [alt]="p.title + ' icon'" class="sticky-nav-product-icon" />
          <ng-template #defaultStickyIcon>
            <i class='bx text-base' [class]="p.icon"></i>
          </ng-template>
          {{ p.title }}
        </a>
      </div>
    </div>
  </nav>

  <!-- ===== PRODUCT SECTIONS ===== -->
  <div class="max-w-7xl mx-auto px-6 py-20 space-y-32">

    <section id="overview" class="section-anchor">

      <!-- Section header -->
      <div class="text-center mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Platform Overview</span>
        <h2 class="text-4xl font-black mt-2 mb-4">What Is ConnectIQ?</h2>
        <p class="text-varroc-text-gray text-lg max-w-3xl mx-auto leading-relaxed">A scalable, cloud-agnostic platform built for OEMs, mobility providers, and enterprises — connecting vehicles, smart devices, industrial IoT, and digital ecosystems into one unified operational layer.</p>
      </div>

      <!-- Highlight stat strip -->
      <div class="grid sm:grid-cols-3 gap-5 mb-14" data-aos="fade-up" data-aos-delay="80">
        <div *ngFor="let item of overviewHighlights" class="feature-card flex flex-col items-center text-center gap-3 !p-7">
          <div class="w-14 h-14 bg-varroc-accent/15 rounded-2xl flex items-center justify-center mb-1">
            <i class='bx icon-gradient text-3xl' [class]="item.icon"></i>
          </div>
          <div class="text-varroc-accent font-black text-xl leading-snug">{{ item.val }}</div>
          <div class="text-varroc-text-gray text-sm leading-relaxed">{{ item.label }}</div>
        </div>
      </div>

      <!-- Body: description + capability cards -->
      <div class="grid lg:grid-cols-2 gap-10 items-start">

        <!-- Left: description -->
        <div data-aos="fade-right">
          <p class="text-varroc-text-gray text-lg leading-relaxed mb-5">ConnectIQ enables seamless integration between vehicles, IoT endpoints, mobile applications, enterprise systems, and cloud infrastructure so organizations can unlock real-time visibility, operational efficiency, and stronger digital experiences.</p>
          <p class="text-varroc-text-gray leading-relaxed mb-8">At its core, ConnectIQ combines device connectivity, telemetry ingestion, analytics, and lifecycle management across large-scale deployments — helping OEMs, mobility providers, and enterprises monitor behavior, optimize operations, and deliver intelligent connected services through one unified ecosystem.</p>

          <!-- Endpoints pill cloud -->
          <div>
            <p class="text-white font-bold text-sm mb-4">Supports Connectivity For</p>
            <div class="flex flex-wrap gap-2">
              <span *ngFor="let item of connectedEndpoints" class="inline-flex items-center gap-2 px-4 py-2 bg-varroc-accent/10 border border-varroc-accent/20 rounded-full text-varroc-accent text-sm font-medium">
                <i class='bx bx-radio-circle-marked text-base'></i>{{ item }}
              </span>
            </div>
          </div>
        </div>

        <!-- Right: capability card -->
        <div class="feature-card !p-7" data-aos="fade-left">
          <div class="flex items-center gap-3 mb-6">
            <div class="w-10 h-10 bg-varroc-accent/15 rounded-xl flex items-center justify-center flex-shrink-0">
              <i class='bx bx-chip icon-gradient text-xl'></i>
            </div>
            <h3 class="text-white font-bold text-lg">Core Platform Capabilities</h3>
          </div>
          <ul class="space-y-4">
            <li *ngFor="let item of overviewCapabilities" class="flex items-start gap-4">
              <div class="w-6 h-6 rounded-full bg-varroc-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i class='bx bx-check icon-gradient text-sm'></i>
              </div>
              <span class="text-varroc-text-gray text-sm leading-relaxed">{{ item }}</span>
            </li>
          </ul>
          <div class="mt-8 pt-6 border-t border-white/10">
            <a routerLink="/contact-us" style="pointer-events: auto;" class="cursor-pointer inline-flex items-center gap-2 bg-varroc-accent text-white text-sm font-bold px-6 py-3 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all">
              Book a Demo <i class='bx bx-right-arrow-alt text-lg'></i>
            </a>
          </div>
        </div>

      </div>
    </section>

    <div class="accent-divider"></div>

    <ng-container *ngFor="let product of products; let i = index; let last = last">
      <section [id]="product.id" class="section-anchor">
        <div class="grid md:grid-cols-2 gap-12 items-start">

          <!-- Description + CTA -->
          <div [ngClass]="i % 2 !== 0 ? 'order-2' : ''" [attr.data-aos]="i % 2 === 0 ? 'fade-right' : 'fade-left'">
            <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest block mb-2">{{ product.subtitle }}</span>
            <h2 class="text-4xl font-black mb-4">{{ product.title }}</h2>
            <p class="text-varroc-text-gray leading-relaxed mb-6">{{ product.desc }}</p>
            <div class="flex flex-wrap gap-2 mb-8">
              <span *ngFor="let tag of product.tags" class="px-3 py-1 bg-varroc-accent/10 border border-varroc-accent/20 rounded-full text-varroc-accent text-xs">{{ tag }}</span>
            </div>
            <a [routerLink]="product.ctaRoute || Routes.contact" class="inline-flex items-center gap-2 bg-varroc-accent text-white font-bold px-6 py-3 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all">
              {{ product.ctaLabel || ('Explore ' + product.title) }} <i class='bx bx-right-arrow-alt text-xl'></i>
            </a>
          </div>

          <!-- Key points -->
          <div [ngClass]="i % 2 !== 0 ? 'order-1' : ''" [attr.data-aos]="i % 2 === 0 ? 'fade-left' : 'fade-right'" class="space-y-4">
            <div *ngFor="let kp of product.keyPoints" class="feature-card flex items-start gap-4 !p-4">
              <div class="w-10 h-10 bg-varroc-accent/15 rounded-lg flex items-center justify-center flex-shrink-0">
                <i class='bx icon-gradient text-xl' [class]="kp.icon"></i>
              </div>
              <div>
                <div class="text-white font-semibold text-sm mb-0.5">{{ kp.title }}</div>
                <div class="text-varroc-text-gray text-xs">{{ kp.desc }}</div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <div class="accent-divider" *ngIf="!last"></div>
    </ng-container>

    <!-- ===== CTA ===== -->
    <!-- <section class="text-center py-16 bg-gradient-to-r from-varroc-accent/10 via-varroc-dark to-varroc-accent/10 border border-varroc-accent/20 rounded-3xl" data-aos="fade-up">
      <h2 class="text-4xl font-black mb-4">Deploy ConnectIQ With Your Product Stack</h2>
      <p class="text-varroc-text-gray text-lg mb-8 max-w-3xl mx-auto">Use a modular rollout model with device integration, streaming, intelligence, security, and engagement layers aligned to your business and compliance needs.</p>
      <div class="flex flex-wrap gap-4 justify-center">
        <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-10 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all text-lg">Talk to Team</a>
        <a routerLink="/resources" class="border border-varroc-accent/40 font-semibold px-10 py-4 rounded-md hover:border-varroc-accent hover:bg-varroc-accent/10 transition-all text-lg">Explore Resources</a>
      </div>
    </section> -->

  </div>

</main>
